import { useState, useCallback, useRef, useEffect } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import ExhaleDashboard from "./ExhaleDashboard";

// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
  name: "Exhale",
  short: "Exhale",
  mission: "Medical billing is predatory. We're the advocates in your corner — catching errors, fighting overcharges, and making sure you never pay more than you owe.",
  tagline: "Take a breath. We've got this.",
};

const ALLOWED_MAGIC = {
  "application/pdf": [0x25, 0x50, 0x44, 0x46],
  "image/jpeg":      [0xff, 0xd8, 0xff],
  "image/png":       [0x89, 0x50, 0x4e, 0x47],
};

const STEPS = [
  { id: "landing",  label: "Home" },
  { id: "signup",   label: "Create Account" },
  { id: "info",     label: "Your Info" },
  { id: "loa",      label: "Authorization" },
  { id: "upload",   label: "Upload Bill" },
  { id: "payment",  label: "Payment" },
  { id: "confirm",  label: "Confirmed" },
];

const CASES_API_URL = "https://rqrjxi4dtzj46lvmd4svxh7ixa0mafny.lambda-url.us-east-2.on.aws/";

// ── Cognito ────────────────────────────────────────────────────────────────
const USER_POOL = new CognitoUserPool({
  UserPoolId: "us-east-2_dJAHbmHQi",
  ClientId:   "6bkglvg6tskfsgl5mbggjet0gl",
});

const ADMIN_POOL = new CognitoUserPool({
  UserPoolId: "us-east-2_3eQw6ITzx",
  ClientId:   "7t5d70l9eh5t6e1nv8b8ebk50o",
});

function getCurrentSession(pool = USER_POOL) {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) { resolve(null); return; }
    user.getSession((err, session) => {
      if (err || !session?.isValid()) { resolve(null); return; }
      resolve({ user, session, email: user.getUsername() });
    });
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────
async function validateMagicBytes(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      for (const [mime, magic] of Object.entries(ALLOWED_MAGIC)) {
        if (magic.every((b, i) => arr[i] === b)) { resolve(mime); return; }
      }
      resolve(null);
    };
    reader.readAsArrayBuffer(file.slice(0, 8));
  });
}

// ── Shared UI ──────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f8f7f5; }
  input, button, textarea { font-family: inherit; }
  input[type=tel], input[type=text], input[type=date], input[type=email], input[type=password] {
    width: 100%; padding: 14px 16px; border: 1.5px solid #ddd;
    border-radius: 8px; font-size: 15px; font-family: 'DM Sans', sans-serif;
    background: #fff; color: #1a1a1a; outline: none; transition: border 0.15s;
  }
  input:focus { border-color: #1a3a5c; }
  label { display: block; font-family: 'DM Mono', monospace; font-size: 11px;
    letter-spacing: 0.12em; color: #888; margin-bottom: 6px; text-transform: uppercase; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

function Shell({ children, step, showProgress = true, loggedIn = false, onLogout, onHome, onSignIn }) {
  const currentIdx = STEPS.findIndex(s => s.id === step);
  const progressPct = showProgress && currentIdx > 0
    ? Math.round((currentIdx / (STEPS.length - 1)) * 100) : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f5", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{css}</style>
      <div style={{ background: "#fff", borderBottom: "1px solid #eee", padding: "0 24px" }}>
        <div style={{ maxWidth: "680px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: "64px" }}>
          <div onClick={onHome} style={{ cursor: onHome ? "pointer" : "default" }}>
            <span style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "17px", color: "#1a3a5c", fontWeight: 700 }}>
              {BRAND.short}
            </span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: "#aaa", letterSpacing: "0.15em", marginLeft: "10px", textTransform: "uppercase" }}>
              Medical Bill Advocacy
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {showProgress && currentIdx > 0 && (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em" }}>
                STEP {currentIdx} OF {STEPS.length - 1}
              </div>
            )}
            {onSignIn && (
              <button onClick={onSignIn} style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.1em", background: "none", border: "1px solid #1a3a5c", borderRadius: "6px", padding: "6px 14px", cursor: "pointer" }}>
                SIGN IN
              </button>
            )}
            {loggedIn && onLogout && (
              <button
                onClick={onLogout}
                style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em", background: "none", border: "none", cursor: "pointer", textTransform: "uppercase" }}
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
        {showProgress && currentIdx > 0 && (
          <div style={{ height: "2px", background: "#f0f0f0" }}>
            <div style={{ height: "100%", width: `${progressPct}%`, background: "#1a3a5c", transition: "width 0.4s ease" }} />
          </div>
        )}
      </div>
      <div style={{ maxWidth: "680px", margin: "0 auto", padding: "48px 24px 80px", animation: "fadeUp 0.3s ease" }}>
        {children}
      </div>
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled = false, style = {} }) {
  const base = {
    padding: "14px 28px", borderRadius: "8px", fontSize: "15px",
    fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer",
    border: "none", transition: "all 0.15s ease", opacity: disabled ? 0.5 : 1, ...style,
  };
  const variants = {
    primary:   { background: "#1a3a5c", color: "#fff" },
    secondary: { background: "#fff", color: "#1a3a5c", border: "1.5px solid #1a3a5c" },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant] }}>{children}</button>;
}

function Heading({ children, sub }) {
  return (
    <div style={{ marginBottom: "32px" }}>
      <h1 style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "32px", color: "#1a1a1a", fontWeight: 400, lineHeight: 1.25, marginBottom: sub ? "10px" : 0 }}>
        {children}
      </h1>
      {sub && <p style={{ fontSize: "15px", color: "#666", lineHeight: 1.6 }}>{sub}</p>}
    </div>
  );
}

// ── Screen 1: Landing ──────────────────────────────────────────────────────
function Landing({ onStart, onLogin, onPromoStart }) {
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");

  const submitPromo = () => {
    if (promoCode.trim().toUpperCase() === "BREATHE26") {
      onPromoStart();
    } else {
      setPromoError("Invalid promo code.");
    }
  };

  return (
    <Shell step="landing" showProgress={false} onSignIn={onLogin}>
      <div style={{ textAlign: "center", padding: "40px 0 56px" }}>
        <h1 style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "48px", fontWeight: 400, color: "#1a1a1a", lineHeight: 1.15, maxWidth: "560px", margin: "0 auto 24px" }}>
          You deserve to know what you're actually being charged.
        </h1>
        <p style={{ fontSize: "17px", color: "#555", lineHeight: 1.75, maxWidth: "480px", margin: "0 auto 16px" }}>
          {BRAND.mission}
        </p>
        <p style={{ fontSize: "20px", color: "#1a3a5c", fontFamily: "'Libre Baskerville', serif", fontStyle: "italic", maxWidth: "480px", margin: "0 auto 40px" }}>
          We are here to break the cycle.
        </p>
        <div style={{ marginBottom: "24px" }}>
          <Btn onClick={onStart} style={{ fontSize: "16px", padding: "16px 40px" }}>
            Review My Bill →
          </Btn>
          <div style={{ marginTop: "12px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em" }}>
            FREE TO START · YOU ONLY PAY IF WE SAVE YOU MONEY
          </div>
        </div>
        <div style={{ marginBottom: "48px" }}>
          <button
            onClick={onLogin}
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#1a3a5c", letterSpacing: "0.1em", textDecoration: "underline" }}
          >
            ALREADY HAVE AN ACCOUNT? SIGN IN
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px", textAlign: "left", borderTop: "1px solid #eee", paddingTop: "48px" }}>
          {[
            { n: "01", title: "Create your account", body: "Takes 2 minutes. Your information is encrypted and HIPAA compliant." },
            { n: "02", title: "Upload your bill", body: "PDF, photo, or image. We handle any format and request itemized records if needed." },
            { n: "03", title: "We fight for you", body: "We negotiate with the hospital. You pay 20% of what we save you — nothing if we don't." },
          ].map(item => (
            <div key={item.n}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#1a3a5c", marginBottom: "8px" }}>{item.n}</div>
              <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "16px", color: "#1a1a1a", marginBottom: "6px" }}>{item.title}</div>
              <div style={{ fontSize: "13px", color: "#777", lineHeight: 1.6 }}>{item.body}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: "48px", background: "#1a3a5c", borderRadius: "12px", padding: "32px", textAlign: "left" }}>
          <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "20px", color: "#fff", fontStyle: "italic", lineHeight: 1.5, marginBottom: "12px" }}>
            "{BRAND.tagline}"
          </div>
          <div style={{ fontSize: "13px", color: "#8baac8", lineHeight: 1.6 }}>
            Medical billing errors affect 8 in 10 hospital bills. The average overcharge is $1,300. We exist because nobody should face a predatory system alone.
          </div>
        </div>

        {/* Promo code — subtle, at the bottom */}
        <div style={{ marginTop: "32px", textAlign: "center" }}>
          {!showPromo ? (
            <button
              onClick={() => setShowPromo(true)}
              style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#ccc", letterSpacing: "0.1em" }}
            >
              HAVE A PROMO CODE?
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", maxWidth: "300px", margin: "0 auto" }}>
              <input
                type="text"
                value={promoCode}
                onChange={e => { setPromoCode(e.target.value); setPromoError(""); }}
                placeholder="Enter promo code"
                style={{ width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontFamily: "'DM Mono', monospace", fontSize: "12px", letterSpacing: "0.1em", textAlign: "center", textTransform: "uppercase" }}
                onKeyDown={e => e.key === "Enter" && submitPromo()}
              />
              {promoError && <div style={{ fontSize: "12px", color: "#c0392b" }}>{promoError}</div>}
              <button
                onClick={submitPromo}
                style={{ background: "#1a3a5c", color: "#fff", border: "none", borderRadius: "8px", padding: "10px 24px", fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "0.1em", cursor: "pointer" }}
              >
                APPLY →
              </button>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ── Login Screen ───────────────────────────────────────────────────────────
function Login({ onLoggedIn, onSignUp, onHome }) {
  const [form, setForm]       = useState({ email: "", password: "" });
  const [stage, setStage]     = useState("login"); // "login" | "forgot" | "reset"
  const [code, setCode]       = useState("");
  const [newPass, setNewPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [info, setInfo]       = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const login = () => {
    if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");

    const user    = new CognitoUser({ Username: form.email, Pool: USER_POOL });
    const details = new AuthenticationDetails({ Username: form.email, Password: form.password });

    user.authenticateUser(details, {
      onSuccess: (session) => {
        setLoading(false);
        onLoggedIn({ email: form.email, user, session });
      },
      onFailure: (err) => {
        setLoading(false);
        if (err.code === "UserNotConfirmedException") {
          setError("Please verify your email first. Check your inbox for the confirmation code.");
        } else if (err.code === "NotAuthorizedException") {
          setError("Incorrect email or password.");
        } else {
          setError(err.message || "Login failed.");
        }
      },
    });
  };

  const sendResetCode = () => {
    if (!form.email) { setError("Enter your email address first."); return; }
    setLoading(true); setError("");
    const user = new CognitoUser({ Username: form.email, Pool: USER_POOL });
    user.forgotPassword({
      onSuccess: () => { setLoading(false); setStage("reset"); setInfo("Code sent — check your email."); },
      onFailure: (err) => { setLoading(false); setError(err.message || "Could not send reset code."); },
    });
  };

  const resetPassword = () => {
    if (!code || !newPass) { setError("Enter the code and your new password."); return; }
    if (newPass.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError("");
    const user = new CognitoUser({ Username: form.email, Pool: USER_POOL });
    user.confirmPassword(code, newPass, {
      onSuccess: () => { setLoading(false); setStage("login"); setInfo("Password reset! Please sign in."); },
      onFailure: (err) => { setLoading(false); setError(err.message || "Reset failed."); },
    });
  };

  if (stage === "forgot" || stage === "reset") return (
    <Shell step="landing" showProgress={false} onHome={onHome}>
      <Heading
        children="Reset your password"
        sub={stage === "forgot" ? "Enter your email and we'll send a reset code." : `Enter the code sent to ${form.email} and your new password.`}
      />
      {stage === "forgot" && (
        <div style={{ marginBottom: "24px" }}>
          <label>Email address</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" />
        </div>
      )}
      {stage === "reset" && (
        <>
          <div style={{ marginBottom: "16px" }}>
            <label>Verification code</label>
            <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" style={{ letterSpacing: "0.3em", fontSize: "22px", textAlign: "center" }} />
          </div>
          <div style={{ marginBottom: "24px" }}>
            <label>New password</label>
            <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="At least 8 characters" />
          </div>
        </>
      )}
      {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      {info  && <div style={{ color: "#1e8449", fontSize: "13px", marginBottom: "16px" }}>{info}</div>}
      <Btn onClick={stage === "forgot" ? sendResetCode : resetPassword} disabled={loading}>
        {loading ? "Sending…" : stage === "forgot" ? "Send Reset Code →" : "Set New Password →"}
      </Btn>
      <div style={{ marginTop: "16px" }}>
        <button onClick={() => { setStage("login"); setError(""); setInfo(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#888", letterSpacing: "0.1em" }}>
          ← BACK TO SIGN IN
        </button>
      </div>
    </Shell>
  );

  return (
    <Shell step="landing" showProgress={false} onHome={onHome}>
      <Heading children="Welcome back" sub="Sign in to check your case status." />
      <div style={{ marginBottom: "16px" }}>
        <label>Email address</label>
        <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" onKeyDown={e => e.key === "Enter" && login()} />
      </div>
      <div style={{ marginBottom: "8px" }}>
        <label>Password</label>
        <input type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="Your password" onKeyDown={e => e.key === "Enter" && login()} />
      </div>
      <div style={{ marginBottom: "24px", textAlign: "right" }}>
        <button onClick={() => { setStage("forgot"); setError(""); }} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.1em" }}>
          FORGOT PASSWORD?
        </button>
      </div>
      {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      {info  && <div style={{ color: "#1e8449", fontSize: "13px", marginBottom: "16px" }}>{info}</div>}
      <Btn onClick={login} disabled={loading} style={{ width: "100%" }}>{loading ? "Signing in…" : "Sign In →"}</Btn>
      <div style={{ marginTop: "24px", textAlign: "center" }}>
        <button onClick={onSignUp} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#888", letterSpacing: "0.1em" }}>
          DON'T HAVE AN ACCOUNT? GET STARTED →
        </button>
      </div>
    </Shell>
  );
}

// ── Patient Dashboard ──────────────────────────────────────────────────────
function PatientDashboard({ authEmail, cognitoUser, onLogout }) {
  const [caseData, setCaseData]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  // Fresh ID token for API calls — getSession auto-refreshes if expired
  const getToken = () => new Promise((resolve) => {
    if (!cognitoUser) { resolve(""); return; }
    cognitoUser.getSession((err, session) => {
      if (err || !session) { resolve(""); return; }
      resolve(session.getIdToken().getJwtToken());
    });
  });

  useEffect(() => {
    async function loadCase() {
      try {
        const idToken = await getToken();
        const res   = await fetch(CASES_API_URL, { headers: { "Authorization": `Bearer ${idToken}` } });
        const data  = await res.json();
        const cases = data.cases || data || [];

        // API verifies the token and returns only this patient's case(s)
        setCaseData(cases.length > 0 ? cases[0] : null);
      } catch (e) {
        // Don't show error if case just hasn't been processed yet
        console.warn("Could not load case data:", e);
      } finally {
        setLoading(false);
      }
    }
    loadCase();
  }, [authEmail]);

  const STATUS_STEPS = [
    { key: "submitted",   label: "Bill Submitted",     desc: "We've received your bill and it's in our queue." },
    { key: "reviewing",   label: "Under Review",       desc: "Our team is analyzing your bill for errors and overcharges." },
    { key: "records",     label: "Requesting Records", desc: "We're requesting your itemized bill from the hospital." },
    { key: "negotiating", label: "Negotiating",        desc: "We're actively negotiating with the hospital on your behalf." },
    { key: "resolved",    label: "Resolved",           desc: "Your case has been resolved successfully." },
  ];

  const deriveStatus = (c) => {
    if (!c) return "submitted";
    // Use admin-set status if available, otherwise default to submitted
    const adminStatus = (c.status || c.case_status || "").toLowerCase();
    if (adminStatus === "negotiating") return "negotiating";
    if (adminStatus === "requesting_records" || adminStatus === "records") return "records";
    if (adminStatus === "under_review" || adminStatus === "reviewing") return "reviewing";
    if (adminStatus === "resolved") return "resolved";
    return "submitted";
  };

  const downloadSummary = async () => {
    if (!caseData?.patient_id) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const idToken = await getToken();
      const res  = await fetch(CASES_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ action: "get_summary_url", patientId: caseData.patient_id }),
      });
      const data = await res.json();
      const parsed = typeof data.body === "string" ? JSON.parse(data.body) : data;
      if (parsed.url) {
        window.open(parsed.url, "_blank");
      } else {
        setDownloadError("Your summary isn't ready yet — check back soon.");
      }
    } catch (e) {
      setDownloadError("Download failed. Please try again.");
    }
    setDownloading(false);
  };


  const currentStatus  = caseData ? deriveStatus(caseData) : "submitted";
  const currentStepIdx = STATUS_STEPS.findIndex(s => s.key === currentStatus);

  const handleLogout = () => {
    cognitoUser?.signOut();
    onLogout();
  };

  if (loading) return (
    <Shell step="landing" showProgress={false} loggedIn onLogout={handleLogout}>
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <div style={{ width: "48px", height: "48px", border: "2px solid #eee", borderTop: "2px solid #1a3a5c", borderRadius: "50%", margin: "0 auto 24px", animation: "spin 1s linear infinite" }} />
        <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "20px", color: "#1a1a1a" }}>Loading your case…</div>
      </div>
    </Shell>
  );

  return (
    <Shell step="landing" showProgress={false} loggedIn onLogout={handleLogout}>

      {/* Header */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.2em", marginBottom: "8px" }}>YOUR CASE</div>
        <h1 style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "36px", color: "#1a1a1a", fontWeight: 400, lineHeight: 1.2, marginBottom: "8px" }}>
          {caseData?.patient_name || "Your Case"}
        </h1>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#888", letterSpacing: "0.1em" }}>
          {authEmail}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fdf2f2", border: "1px solid #f5c6c6", borderRadius: "8px", padding: "16px", marginBottom: "24px", fontSize: "14px", color: "#c0392b" }}>
          {error}
        </div>
      )}

      {/* Status tracker */}
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "12px", padding: "28px", marginBottom: "24px" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.2em", marginBottom: "24px" }}>CASE STATUS</div>
        {STATUS_STEPS.map((step, i) => {
          const done    = i < currentStepIdx;
          const active  = i === currentStepIdx;
          const pending = i > currentStepIdx;
          return (
            <div key={step.key} style={{ display: "flex", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{
                  width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: done || active ? "#1a3a5c" : "#f0f0f0",
                  fontSize: "13px", color: done || active ? "#fff" : "#ccc",
                  fontFamily: "'DM Mono', monospace",
                }}>
                  {done ? "✓" : i + 1}
                </div>
                {i < STATUS_STEPS.length - 1 && (
                  <div style={{ width: "2px", flex: 1, minHeight: "24px", background: done ? "#1a3a5c" : "#f0f0f0", margin: "4px 0" }} />
                )}
              </div>
              <div style={{ paddingBottom: i < STATUS_STEPS.length - 1 ? "20px" : "0", paddingTop: "4px" }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", fontWeight: 500, color: pending ? "#ccc" : "#1a1a1a", marginBottom: "2px" }}>
                  {step.label}
                  {active && (
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: "#1a3a5c", letterSpacing: "0.1em", marginLeft: "10px", background: "#eef3f8", padding: "2px 8px", borderRadius: "4px" }}>
                      CURRENT
                    </span>
                  )}
                </div>
                {(done || active) && (
                  <div style={{ fontSize: "13px", color: "#888", lineHeight: 1.5 }}>{step.desc}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bill details */}
      {caseData && (
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "12px", padding: "28px", marginBottom: "24px" }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.2em", marginBottom: "20px" }}>BILL DETAILS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            {caseData.source_file && (
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em", marginBottom: "4px" }}>FILE</div>
                <div style={{ fontSize: "14px", color: "#1a1a1a", wordBreak: "break-all" }}>{caseData.source_file}</div>
              </div>
            )}
            {caseData.total_billed && (
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em", marginBottom: "4px" }}>TOTAL BILLED</div>
                <div style={{ fontSize: "14px", color: "#1a1a1a" }}>{caseData.total_billed}</div>
              </div>
            )}

            {caseData.analyzed_at && (
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em", marginBottom: "4px" }}>SUBMITTED</div>
                <div style={{ fontSize: "14px", color: "#1a1a1a" }}>
                  {new Date(caseData.analyzed_at.replace(/_/g, "T").replace(/T(\d{2})-(\d{2})-(\d{2})$/, "T$1:$2:$3")).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Case summary download — only show when advocate notes are saved */}
      {caseData?.advocate_notes && (
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "12px", padding: "28px", marginBottom: "24px" }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.2em", marginBottom: "12px" }}>CASE SUMMARY</div>
          <div style={{ fontSize: "14px", color: "#555", lineHeight: 1.6, marginBottom: "20px" }}>
            Your advocate has added notes to your case. Download a copy of your case summary for your records.
          </div>
          <button
            onClick={downloadSummary}
            disabled={downloading}
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              padding: "12px 24px", background: downloading ? "#ccc" : "#1a3a5c",
              color: "#fff", border: "none", borderRadius: "8px",
              fontFamily: "'DM Mono', monospace", fontSize: "11px",
              letterSpacing: "0.1em", cursor: downloading ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {downloading ? "PREPARING…" : "↓ DOWNLOAD CASE SUMMARY"}
          </button>
          {downloadError && (
            <div style={{ marginTop: "10px", fontSize: "13px", color: "#c0392b" }}>{downloadError}</div>
          )}
        </div>
      )}

      {/* What happens next */}
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "12px", padding: "28px", marginBottom: "24px" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.2em", marginBottom: "20px" }}>WHAT HAPPENS NEXT</div>
        {[
          { step: "1–2 business days", action: "We review your bill and call you to discuss findings" },
          { step: "3–5 business days", action: "We request itemized records and build your case" },
          { step: "1–2 weeks",         action: "We negotiate directly with the hospital billing department" },
          { step: "Upon resolution",   action: "We charge 20% of savings and send you the corrected statement" },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", gap: "16px", marginBottom: i < 3 ? "16px" : 0, paddingBottom: i < 3 ? "16px" : 0, borderBottom: i < 3 ? "1px solid #f0f0f0" : "none" }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.06em", flexShrink: 0, paddingTop: "2px", width: "140px" }}>{item.step}</div>
            <div style={{ fontSize: "14px", color: "#555" }}>{item.action}</div>
          </div>
        ))}
      </div>

      {/* Contact */}
      <div style={{ background: "#1a3a5c", borderRadius: "12px", padding: "28px" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#4a6a8a", letterSpacing: "0.2em", marginBottom: "16px" }}>CONTACT YOUR ADVOCATE</div>
        <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "18px", color: "#fff", marginBottom: "8px" }}>
          Questions about your case?
        </div>
        <div style={{ fontSize: "14px", color: "#8baac8", lineHeight: 1.6, marginBottom: "20px" }}>
          We'll reach out within 1–2 business days. If you have urgent questions, email us directly.
        </div>
        <a
          href="mailto:help@exhale.care"
          style={{ display: "inline-block", background: "#fff", color: "#1a3a5c", padding: "12px 24px", borderRadius: "8px", fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "0.1em", textDecoration: "none", fontWeight: 500 }}
        >
          EMAIL US →
        </a>
      </div>

    </Shell>
  );
}

// ── Screen 2: Create Account ───────────────────────────────────────────────
function CreateAccount({ onNext, onHome }) {
  const [form, setForm]               = useState({ email: "", password: "", confirm: "" });
  const [code, setCode]               = useState("");
  const [stage, setStage]             = useState("signup");
  const [cognitoUser, setCognitoUser] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.email || !form.password) { setError("Please fill in all fields."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError("Please enter a valid email address."); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords don't match."); return; }
    setLoading(true); setError("");

    USER_POOL.signUp(form.email, form.password, [], null, (err, result) => {
      setLoading(false);
      if (err) {
        if (err.code === "UsernameExistsException") {
          const user = new CognitoUser({ Username: form.email, Pool: USER_POOL });
          setCognitoUser(user);
          setStage("verify");
          setError("Account exists — enter the code we sent to your email.");
        } else {
          setError(err.message || "Sign-up failed. Please try again.");
        }
        return;
      }
      setCognitoUser(result.user);
      setStage("verify");
    });
  };

  const verify = () => {
    if (!code) { setError("Please enter the 6-digit code."); return; }
    setLoading(true); setError("");
    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) { setLoading(false); setError(err.message || "Invalid code. Please try again."); return; }
      // Auto sign-in after verification so we have an active session
      const authDetails = new AuthenticationDetails({ Username: form.email, Password: form.password });
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: () => { setLoading(false); onNext({ email: form.email, cognitoUser }); },
        onFailure: () => { setLoading(false); onNext({ email: form.email, cognitoUser }); },
      });
    });
  };

  const resend = () => {
    if (!cognitoUser) return;
    cognitoUser.resendConfirmationCode((err) => {
      setError(err ? "Could not resend — try again in a moment." : "Code resent! Check your inbox.");
    });
  };

  if (stage === "verify") return (
    <Shell step="signup" onHome={onHome}>
      <Heading children="Check your email" sub={`We sent a 6-digit verification code to ${form.email}. Check your spam folder if you don't see it.`} />
      <div style={{ marginBottom: "24px" }}>
        <label>Verification code *</label>
        <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" style={{ letterSpacing: "0.3em", fontSize: "22px", textAlign: "center" }} />
      </div>
      {error && <div style={{ color: error.startsWith("Code resent") ? "#1e8449" : "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      <Btn onClick={verify} disabled={loading}>{loading ? "Verifying…" : "Verify Email →"}</Btn>
      <div style={{ marginTop: "20px" }}>
        <span onClick={resend} style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.1em", cursor: "pointer", textDecoration: "underline" }}>
          RESEND CODE
        </span>
      </div>
    </Shell>
  );

  return (
    <Shell step="signup" onHome={onHome}>
      <Heading children="Create your account" sub="Your information is encrypted and protected under HIPAA. We will never sell your data." />
      <div style={{ marginBottom: "16px" }}>
        <label>Email address *</label>
        <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" />
      </div>
      <div style={{ marginBottom: "16px" }}>
        <label>Password *</label>
        <input type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="At least 8 characters" />
      </div>
      <div style={{ marginBottom: "24px" }}>
        <label>Confirm password *</label>
        <input type="password" value={form.confirm} onChange={e => set("confirm", e.target.value)} placeholder="Repeat your password" />
      </div>
      {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      <Btn onClick={submit} disabled={loading}>{loading ? "Creating account…" : "Create Account →"}</Btn>
      <div style={{ marginTop: "24px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#bbb", letterSpacing: "0.1em" }}>
        HIPAA COMPLIANT · 256-BIT ENCRYPTION · WE NEVER SELL YOUR DATA
      </div>
    </Shell>
  );
}

// ── Screen 3: Personal Info ────────────────────────────────────────────────
function PersonalInfo({ onNext, onHome }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", dob: "", phone: "", insurance: "" });
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const submit = () => {
    if (!form.firstName || !form.lastName || !form.dob || !form.phone) {
      setError("Please fill in all required fields."); return;
    }
    if (form.phone.replace(/\D/g, "").length !== 10) {
      setError("Please enter a valid 10-digit phone number."); return;
    }
    onNext(form);
  };

  return (
    <Shell step="info" onHome={onHome}>
      <Heading children="Tell us about yourself" sub="This helps us verify your identity when we contact the hospital on your behalf." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
        <div><label>First name *</label><input type="text" value={form.firstName} onChange={e => set("firstName", e.target.value)} placeholder="Sarah" /></div>
        <div><label>Last name *</label><input type="text" value={form.lastName} onChange={e => set("lastName", e.target.value)} placeholder="Thompson" /></div>
      </div>
      <div style={{ marginBottom: "16px" }}>
        <label>Date of birth *</label>
        <input type="date" value={form.dob} onChange={e => set("dob", e.target.value)} />
      </div>
      <div style={{ marginBottom: "16px" }}>
        <label>Mobile phone number *</label>
        <input type="tel" value={form.phone} onChange={e => set("phone", formatPhone(e.target.value))} placeholder="(801) 555-0100" />
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", marginTop: "6px", letterSpacing: "0.08em" }}>
          THIS WILL BE YOUR TWO-FACTOR AUTHENTICATION
        </div>
      </div>
      <div style={{ marginBottom: "24px" }}>
        <label>Insurance provider (optional)</label>
        <input type="text" value={form.insurance} onChange={e => set("insurance", e.target.value)} placeholder="e.g. BlueCross BlueShield PPO" />
      </div>
      {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      <Btn onClick={submit}>Continue →</Btn>
    </Shell>
  );
}

// ── Screen 4: LOA ──────────────────────────────────────────────────────────
function LOASigning({ patient, isPromo, onSigned, onHome }) {
  const [signed, setSigned] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError]   = useState("");
  const fullName = `${patient.firstName} ${patient.lastName}`;
  const LOA_URL  = "https://shcnlenbymfozqbudjom5bfuua0mqnye.lambda-url.us-east-2.on.aws/";
  const LOA_TEXT = `I, ${fullName}, hereby authorize Exhale to: access my medical billing records and explanation of benefits; communicate with my healthcare providers and insurance company on my behalf; negotiate medical bill amounts and payment arrangements; request itemized bills and supporting documentation. I understand that Exhale will charge a success fee of 20% of verified savings, collected only upon successful negotiation. This authorization is valid for 12 months from the date signed and may be revoked in writing at any time.`;

  const submit = async () => {
    if (!agreed) { setError("Please check the authorization checkbox."); return; }
    if (signed.trim().toLowerCase() !== fullName.toLowerCase()) {
      setError(`Please type your full name exactly: ${fullName}`); return;
    }
    const signedAt = new Date().toISOString();
    const caseId   = patient.lastName.toLowerCase().replace(/\s+/g, "_") + "_" + Date.now();
    try {
      await fetch(LOA_URL, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId, fullName, phone: patient.phone || "", signedAt, loaText: LOA_TEXT }),
      });
    } catch (e) { console.warn("LOA save failed:", e); }
    onSigned({ signedAt, name: fullName, caseId });
  };

  return (
    <Shell step="loa" onHome={onHome}>
      <Heading children="Authorize us to act on your behalf" sub="Federal law (HIPAA) requires your written authorization before we can access your billing records or speak with your hospital." />
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "10px", padding: "24px", marginBottom: "24px", fontSize: "14px", color: "#555", lineHeight: 1.75 }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.15em", marginBottom: "14px" }}>LETTER OF AUTHORIZATION</div>
        <p style={{ marginBottom: "12px" }}>
          I, <strong style={{ color: "#1a1a1a" }}>{fullName}</strong>, hereby authorize <strong style={{ color: "#1a1a1a" }}>Exhale</strong> to:
        </p>
        <ul style={{ paddingLeft: "20px", marginBottom: "12px" }}>
          <li style={{ marginBottom: "6px" }}>Access my medical billing records and explanation of benefits</li>
          <li style={{ marginBottom: "6px" }}>Communicate with my healthcare providers and insurance company on my behalf</li>
          <li style={{ marginBottom: "6px" }}>Negotiate medical bill amounts and payment arrangements</li>
          <li style={{ marginBottom: "6px" }}>Request itemized bills and supporting documentation</li>
        </ul>
        <p style={{ marginBottom: "12px" }}>
          {isPromo ? "I understand that Exhale will review my bill and send me a detailed report at no charge." : <span>I understand that Exhale will charge a success fee of <strong style={{ color: "#1a1a1a" }}>20% of verified savings</strong>, collected only upon successful negotiation. Corrected billing documentation will be released upon successful payment of the success fee.</span>}
        </p>
        <p style={{ fontSize: "12px", color: "#aaa" }}>
          This authorization is valid for 12 months from the date signed and may be revoked in writing at any time.
        </p>
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: "#333", letterSpacing: 0, textTransform: "none" }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: "2px", width: "16px", height: "16px", flexShrink: 0 }} />
          I have read and agree to the Letter of Authorization above. I authorize Exhale to act on my behalf.
        </label>
      </div>
      <div style={{ marginBottom: "24px" }}>
        <label>Type your full name to sign *</label>
        <input type="text" value={signed} onChange={e => setSigned(e.target.value)} placeholder={fullName} />
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", marginTop: "6px" }}>
          TYPE EXACTLY: {fullName.toUpperCase()}
        </div>
      </div>
      {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      <Btn onClick={submit}>Sign & Continue →</Btn>
      <div style={{ marginTop: "16px", fontSize: "12px", color: "#aaa", lineHeight: 1.6 }}>
        Your signed authorization is stored securely and encrypted.
      </div>
    </Shell>
  );
}

// ── Screen 5: Upload ───────────────────────────────────────────────────────
function UploadBill({ patient, cognitoUser, patientEmail, isPromo, onUploaded, onHome }) {
  const [drag, setDrag]     = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError]   = useState("");
  const inputRef            = useRef();

  const UPLOAD_API = "https://b6l6d5cw5e5m2dssmvn7ngtfvm0raoqj.lambda-url.us-east-2.on.aws/";

  const handleFile = useCallback(async (file) => {
    setError(""); setStatus("validating");

    // Client-side checks
    if (file.size > 10 * 1024 * 1024) { setError("File too large. Maximum 10MB."); setStatus("idle"); return; }
    const mime = await validateMagicBytes(file);
    if (!mime) { setError("Invalid file type. Only PDF, JPG, and PNG are accepted."); setStatus("idle"); return; }

    // Block double extensions client-side too
    const blockedExts = /\.(exe|bat|cmd|sh|php|js|vbs|ps1|dll|so|jar|html|xml|svg)$/i;
    if (blockedExts.test(file.name)) {
      setError("This file type is not allowed."); setStatus("idle"); return;
    }

    setStatus("uploading");

    try {
      // Get Cognito token for auth
      let idToken = "";
      const currentUser = cognitoUser || USER_POOL.getCurrentUser();
      if (currentUser) {
        await new Promise((resolve) => {
          currentUser.getSession((err, session) => {
            if (!err && session) idToken = session.getIdToken().getJwtToken();
            resolve();
          });
        });
      }

      // Request presigned URL from our secure Lambda
      const res = await fetch(UPLOAD_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ filename: file.name, contentType: mime, fileSize: file.size, patientId: patient?.lastName || "", isPromo: isPromo || false, patientEmail: patientEmail || patient?.email || "" }),
      });
      const data = await res.json();
      if (!res.ok || !data.uploadUrl) {
        setError(data.error || "Upload request failed. Please try again."); setStatus("idle"); return;
      }

      // Upload directly to S3 using presigned URL
      const uploadRes = await fetch(data.uploadUrl, {
        method: "PUT", body: file, headers: { "Content-Type": mime },
      });
      if (!uploadRes.ok) { setError("Upload failed. Please try again."); setStatus("idle"); return; }

      onUploaded(data.s3Key);
    } catch (e) { setError("Upload failed. Please check your connection and try again."); setStatus("idle"); return; }
  }, [onUploaded, patient]);

  return (
    <Shell step="upload" onHome={onHome}>
      <Heading children="Upload your medical bill" sub="Don't have an itemized bill? No problem — upload whatever you have. We'll request the full itemized version from the hospital on your behalf." />
      {status === "idle" ? (
        <div>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
            style={{ border: `2px dashed ${drag ? "#1a3a5c" : "#ccc"}`, borderRadius: "12px", padding: "56px 32px", textAlign: "center", cursor: "pointer", background: drag ? "#f0f4f8" : "#fff", transition: "all 0.2s" }}
          >
            <div style={{ fontSize: "40px", marginBottom: "16px" }}>📄</div>
            <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "18px", color: "#1a1a1a", marginBottom: "8px" }}>
              Drop your bill here or click to browse
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#aaa", letterSpacing: "0.1em" }}>
              PDF · JPG · PNG · MAX 10MB
            </div>
            <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
              onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
          </div>
          {error && (
            <div style={{ marginTop: "16px", background: "#fdf2f2", border: "1px solid #f5c6c6", borderRadius: "8px", padding: "14px 16px", fontSize: "14px", color: "#c0392b" }}>⚠ {error}</div>
          )}
          <div style={{ marginTop: "24px", background: "#f8f9fa", borderRadius: "8px", padding: "16px", fontSize: "13px", color: "#666", lineHeight: 1.6 }}>
            <strong style={{ color: "#1a1a1a" }}>Don't have an itemized bill?</strong> Most hospitals only send a summary statement. Upload that — we'll request the full itemized bill from the hospital as the first step in your case.
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ width: "48px", height: "48px", border: "2px solid #eee", borderTop: "2px solid #1a3a5c", borderRadius: "50%", margin: "0 auto 24px", animation: "spin 1s linear infinite" }} />
          <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "22px", color: "#1a1a1a", marginBottom: "8px" }}>
            {status === "validating" ? "Checking file security..." : "Uploading securely..."}
          </div>
        </div>
      )}
    </Shell>
  );
}

// ── Screen 6: Payment ──────────────────────────────────────────────────────
const STRIPE_PK         = "pk_live_51T99m7Rp8qC7j2vFXXnKFAsoQa5VNSixpw2alc5FZ2KludjJmbiSNKQ8AYfPFgnKYlqU9XaFwzx4wQaIyuqIOq6x00FH2x3j8x";
const STRIPE_LAMBDA_URL = "https://aj2ur7yqqpktomxm3yb25mpc340duifs.lambda-url.us-east-2.on.aws/";

let stripePromise = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = new Promise((resolve) => {
      const script  = document.createElement("script");
      script.src    = "https://js.stripe.com/v3/";
      script.onload = () => resolve(window.Stripe(STRIPE_PK));
      document.head.appendChild(script);
    });
  }
  return stripePromise;
}

function PaymentSetup({ patient, s3Key, onComplete, onHome }) {
  const [mode, setMode]       = useState("consumer");
  const [form, setForm]       = useState({ name: "", company: "", billingEmail: "" });
  const [agreed, setAgreed]   = useState(false);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const stripeRef      = useRef(null);
  const cardElementRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const mountStripeCard = useCallback(async (el) => {
    if (!el || mode !== "consumer") return;
    const stripe      = await getStripe();
    stripeRef.current = stripe;
    const elements    = stripe.elements();
    const card        = elements.create("card", {
      style: { base: { fontSize: "15px", fontFamily: "'DM Sans', sans-serif", color: "#1a1a1a", "::placeholder": { color: "#aaa" } }, invalid: { color: "#c0392b" } },
    });
    card.mount(el);
    cardElementRef.current = card;
  }, [mode]);

  const submit = async () => {
    if (!agreed) { setError("Please agree to the payment authorization."); return; }
    setLoading(true); setError("");
    try {
      if (mode === "consumer") {
        if (!form.name) { setError("Please enter the name on your card."); setLoading(false); return; }
        const stripe = stripeRef.current;
        if (!stripe || !cardElementRef.current) { setError("Payment form not ready. Please refresh."); setLoading(false); return; }
        const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
          type: "card", card: cardElementRef.current,
          billing_details: { name: form.name, phone: patient?.phone || "" },
        });
        if (stripeError) { setError(stripeError.message); setLoading(false); return; }
        const res  = await fetch(STRIPE_LAMBDA_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "save_card", paymentMethodId: paymentMethod.id, name: `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim(), phone: patient?.phone || "", caseId: patient?.lastName?.toLowerCase() + "_" + Date.now(), s3Key: s3Key || "" }),
        });
        const data = await res.json();
        const parsed = typeof data.body === "string" ? JSON.parse(data.body) : data;
        if (!parsed.success) { setError(parsed.error || "Failed to save card."); setLoading(false); return; }
      } else {
        if (!form.company || !form.billingEmail) { setError("Please fill in company name and billing email."); setLoading(false); return; }
        const res  = await fetch(STRIPE_LAMBDA_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "create_b2b", company: form.company, billingEmail: form.billingEmail, caseId: form.company.toLowerCase().replace(/\s+/g, "_") + "_" + Date.now(), s3Key: s3Key || "" }),
        });
        const data = await res.json();
        const parsed = typeof data.body === "string" ? JSON.parse(data.body) : data;
        if (!parsed.success) { setError(parsed.error || "Failed to set up billing."); setLoading(false); return; }
      }
      onComplete();
    } catch (e) { setError("Something went wrong. Please try again."); setLoading(false); }
  };

  const tabStyle = (t) => ({
    flex: 1, padding: "12px", border: "none", borderRadius: "8px", cursor: "pointer",
    fontFamily: "'DM Mono', monospace", fontSize: "11px", letterSpacing: "0.1em",
    background: mode === t ? "#1a3a5c" : "transparent", color: mode === t ? "#fff" : "#888", transition: "all 0.15s",
  });

  return (
    <Shell step="payment" onHome={onHome}>
      <Heading children="Payment setup" sub="You won't be charged until we successfully negotiate your bill." />
      <div style={{ display: "flex", background: "#f0f0f0", borderRadius: "10px", padding: "4px", marginBottom: "24px" }}>
        <button style={tabStyle("consumer")} onClick={() => setMode("consumer")}>INDIVIDUAL</button>
        <button style={tabStyle("b2b")} onClick={() => setMode("b2b")}>BUSINESS / EMPLOYER</button>
      </div>
      {mode === "consumer" ? (
        <>
          <div style={{ background: "#f0faf4", border: "1px solid #b8e8cb", borderRadius: "8px", padding: "14px 16px", marginBottom: "24px", fontSize: "14px", color: "#1e8449" }}>
            ✓ No charge today. Your card is saved securely via Stripe and only charged after a successful negotiation.
          </div>
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "10px", padding: "24px", marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label>Name on card *</label>
              <input type="text" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Sarah Thompson" />
            </div>
            <div>
              <label>Card details *</label>
              <div ref={mountStripeCard} style={{ border: "1.5px solid #ddd", borderRadius: "8px", padding: "14px 16px", background: "#fff" }} />
            </div>
          </div>
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: "#333", letterSpacing: 0, textTransform: "none" }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: "2px", width: "16px", height: "16px", flexShrink: 0 }} />
              I authorize Exhale to charge this card 20% of verified savings at the time a negotiated settlement is reached, prior to my payment to the hospital. Corrected billing documentation will be released upon receipt of Exhale's fee.
            </label>
          </div>
        </>
      ) : (
        <>
          <div style={{ background: "#f0f4fa", border: "1px solid #b8cce8", borderRadius: "8px", padding: "14px 16px", marginBottom: "24px", fontSize: "14px", color: "#1a3a5c" }}>
            ✓ We'll send your billing department a Stripe invoice after successful negotiation. Net-30 terms available.
          </div>
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "10px", padding: "24px", marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label>Company / Employer name *</label>
              <input type="text" value={form.company} onChange={e => set("company", e.target.value)} placeholder="Acme Corporation" />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label>Billing contact email *</label>
              <input type="email" value={form.billingEmail} onChange={e => set("billingEmail", e.target.value)} placeholder="ap@acmecorp.com" />
            </div>
          </div>
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: "#333", letterSpacing: 0, textTransform: "none" }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: "2px", width: "16px", height: "16px", flexShrink: 0 }} />
              I authorize Exhale to invoice our company for 20% of verified savings at the time a negotiated settlement is reached, prior to the patient's payment to the hospital.
            </label>
          </div>
        </>
      )}
      {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
      <Btn onClick={submit} disabled={loading}>
        {loading ? "Submitting..." : mode === "consumer" ? "Save Card & Continue →" : "Submit & Request Invoice →"}
      </Btn>
      <div style={{ marginTop: "16px" }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.1em" }}>
          🔒 SECURED BY STRIPE · PCI COMPLIANT · HIPAA COMPLIANT
        </span>
      </div>
    </Shell>
  );
}

// ── Screen 7: Confirmation ─────────────────────────────────────────────────
function Confirmation({ patient, onHome, onDashboard, isPromo }) {
  return (
    <Shell step="confirm" onHome={onHome}>
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ width: "72px", height: "72px", background: "#f0faf4", border: "2px solid #b8e8cb", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 32px", fontSize: "32px" }}>
          ✓
        </div>
        {isPromo ? (
          <>
            <h1 style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "40px", color: "#1a1a1a", fontWeight: 400, lineHeight: 1.2, maxWidth: "540px", margin: "0 auto 24px" }}>
              Check your email for details on how to fight your bill.
            </h1>
            <p style={{ fontSize: "17px", color: "#999", lineHeight: 1.6, maxWidth: "400px", margin: "0 auto 56px" }}>
              We've analyzed your bill and sent you a detailed breakdown with exactly what to say when you call the hospital.
            </p>
          </>
        ) : (
          <>
            <h1 style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "40px", color: "#1a1a1a", fontWeight: 400, lineHeight: 1.2, maxWidth: "540px", margin: "0 auto 24px" }}>
              We'll review your bill, find every error, and call you within 1–2 business days.
            </h1>
            <p style={{ fontSize: "17px", color: "#999", lineHeight: 1.6, maxWidth: "400px", margin: "0 auto 56px" }}>
              You won't be charged until we successfully negotiate.
            </p>
          </>
        )}
        {!isPromo && <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "10px", padding: "24px", textAlign: "left", marginBottom: "32px" }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.2em", marginBottom: "16px" }}>WHAT HAPPENS NEXT</div>
          {[
            { step: "1–2 business days", action: "We review your bill and call you to discuss findings" },
            { step: "3–5 business days", action: "We request itemized records and build your case" },
            { step: "1–2 weeks",         action: "We negotiate directly with the hospital billing department" },
            { step: "Upon resolution",   action: "We charge 20% of savings and send you the corrected statement" },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", gap: "16px", marginBottom: i < 3 ? "16px" : 0, paddingBottom: i < 3 ? "16px" : 0, borderBottom: i < 3 ? "1px solid #f0f0f0" : "none" }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.06em", flexShrink: 0, paddingTop: "2px", width: "140px" }}>{item.step}</div>
              <div style={{ fontSize: "14px", color: "#555" }}>{item.action}</div>
            </div>
          ))}
        </div>}
        <div style={{ background: "#1a3a5c", borderRadius: "10px", padding: "28px" }}>
          <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "20px", color: "#fff", fontStyle: "italic", marginBottom: "8px" }}>
            "{BRAND.tagline}"
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#4a6a8a", letterSpacing: "0.1em" }}>
            EXHALE · MEDICAL BILL ADVOCACY
          </div>
        </div>
        {!isPromo && <button
          onClick={onDashboard}
          style={{ marginTop: "24px", width: "100%", padding: "16px", background: "#fff", border: "2px solid #1a3a5c", borderRadius: "10px", fontFamily: "'DM Mono', monospace", fontSize: "12px", color: "#1a3a5c", letterSpacing: "0.12em", cursor: "pointer", fontWeight: 600 }}
        >
          VIEW MY DASHBOARD →
        </button>}
      </div>
    </Shell>
  );
}


// Admin Login Screen
function AdminLogin({ onLoggedIn }) {
  const [form, setForm]   = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("login"); // "login" | "forgot" | "reset"
  const [code, setCode]   = useState("");
  const [newPass, setNewPass] = useState("");
  const [info, setInfo]   = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sendResetCode = () => {
    if (!form.email) { setError("Please enter your email."); return; }
    setLoading(true); setError("");
    const user = new CognitoUser({ Username: form.email, Pool: ADMIN_POOL });
    user.forgotPassword({
      onSuccess: () => { setLoading(false); setStage("reset"); setInfo("Code sent — check your email."); },
      onFailure: (err) => { setLoading(false); setError(err.message || "Could not send reset code."); },
    });
  };

  const resetPassword = () => {
    if (!code || !newPass) { setError("Please enter the code and new password."); return; }
    setLoading(true); setError("");
    const user = new CognitoUser({ Username: form.email, Pool: ADMIN_POOL });
    user.confirmPassword(code, newPass, {
      onSuccess: () => { setLoading(false); setStage("login"); setInfo("Password reset! Please sign in."); },
      onFailure: (err) => { setLoading(false); setError(err.message || "Reset failed."); },
    });
  };

  if (stage === "forgot" || stage === "reset") return (
    <div style={{ minHeight: "100vh", background: "#1a3a5c", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{css}</style>
      <div style={{ background: "#fff", borderRadius: "12px", padding: "48px 40px", width: "100%", maxWidth: "400px", boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ marginBottom: "32px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "22px", color: "#1a3a5c", fontWeight: 700, marginBottom: "4px" }}>Exhale</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.15em" }}>RESET PASSWORD</div>
        </div>
        {stage === "forgot" && (
          <div style={{ marginBottom: "16px" }}>
            <label>Email address</label>
            <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" />
          </div>
        )}
        {stage === "reset" && (
          <>
            <div style={{ marginBottom: "16px" }}>
              <label>Reset code</label>
              <input type="text" value={code} onChange={e => setCode(e.target.value)} placeholder="6-digit code" />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label>New password</label>
              <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="New password" />
            </div>
          </>
        )}
        {info && <div style={{ color: "#1e8449", fontSize: "13px", marginBottom: "16px" }}>{info}</div>}
        {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
        <button onClick={stage === "forgot" ? sendResetCode : resetPassword} disabled={loading}
          style={{ width: "100%", padding: "14px", background: "#1a3a5c", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 500, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? "Sending…" : stage === "forgot" ? "Send Reset Code →" : "Set New Password →"}
        </button>
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <button onClick={() => { setStage("login"); setError(""); setInfo(""); }}
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.1em" }}>
            BACK TO LOGIN
          </button>
        </div>
      </div>
    </div>
  );

  const login = () => {
    if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");

    const user    = new CognitoUser({ Username: form.email, Pool: ADMIN_POOL });
    const details = new AuthenticationDetails({ Username: form.email, Password: form.password });

    user.authenticateUser(details, {
      onSuccess: (session) => {
        setLoading(false);
        onLoggedIn({ email: form.email, user, session });
      },
      onFailure: (err) => {
        setLoading(false);
        if (err.code === "NotAuthorizedException") {
          setError("Incorrect email or password.");
        } else {
          setError(err.message || "Login failed.");
        }
      },
      mfaRequired: () => {
        setLoading(false);
        const code = prompt("Enter the MFA code sent to your phone:");
        if (code) {
          setLoading(true);
          user.sendMFACode(code, {
            onSuccess: (session) => {
              setLoading(false);
              onLoggedIn({ email: form.email, user, session });
            },
            onFailure: (err) => {
              setLoading(false);
              setError(err.message || "Invalid MFA code.");
            },
          });
        }
      },
    });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1a3a5c", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{css}</style>
      <div style={{ background: "#fff", borderRadius: "12px", padding: "48px 40px", width: "100%", maxWidth: "400px", boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ marginBottom: "32px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Libre Baskerville', serif", fontSize: "22px", color: "#1a3a5c", fontWeight: 700, marginBottom: "4px" }}>Exhale</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#aaa", letterSpacing: "0.15em" }}>ADMIN PORTAL</div>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <label>Email address</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
            placeholder="you@example.com" onKeyDown={e => e.key === "Enter" && login()} />
        </div>
        <div style={{ marginBottom: "24px" }}>
          <label>Password</label>
          <input type="password" value={form.password} onChange={e => set("password", e.target.value)}
            placeholder="Your password" onKeyDown={e => e.key === "Enter" && login()} />
        </div>
        {error && <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
        <button
          onClick={disabled => loading ? null : login()}
          disabled={loading}
          style={{ width: "100%", padding: "14px", background: "#1a3a5c", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 500, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}
        >
          {loading ? "Signing in…" : "Sign In →"}
        </button>
        {info && <div style={{ color: "#1e8449", fontSize: "13px", marginTop: "12px", textAlign: "center" }}>{info}</div>}
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <button onClick={() => { setStage("forgot"); setError(""); setInfo(""); }}
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#1a3a5c", letterSpacing: "0.1em" }}>
            FORGOT PASSWORD?
          </button>
        </div>
        <div style={{ marginTop: "16px", textAlign: "center", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#ccc", letterSpacing: "0.1em" }}>
          EXHALE ADMIN · AUTHORIZED ACCESS ONLY
        </div>
      </div>
    </div>
  );
}

// ── App Router ─────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen]   = useState("loading");
  const [account, setAccount] = useState(null);
  const [patient, setPatient] = useState(null);
  const [auth, setAuth]       = useState(null); // { email, user, session }
  const [s3Key, setS3Key]     = useState(null); // S3 key of uploaded bill

  // Promo code detection — set when user enters BREATHE26 on landing screen
  const [isPromo, setIsPromo] = useState(false);

  // Check for existing Cognito session on mount
  useEffect(() => {
    const isAdmin = window.location.search.includes("admin");
    if (isAdmin) {
      getCurrentSession(ADMIN_POOL).then(session => {
        if (session) {
          setAuth(session);
          setScreen("admin-dashboard");
        } else {
          setScreen("admin-login");
        }
      });
    } else {
      getCurrentSession(USER_POOL).then(session => {
        if (session) {
          setAuth(session);
          setScreen("dashboard");
        } else {
          setScreen("landing");
        }
      });
    }
  }, []);

  const handleLoggedIn = (authData) => {
    setAuth(authData);
    setScreen("dashboard");
  };

  const handleLogout = () => {
    setAuth(null);
    setScreen("landing");
  };

  // Admin route — gated by Cognito admin pool
  const isVeltaxio = window.location.hostname.includes("veltaxio");
  const isAdminRoute = window.location.search.includes("admin");

  // Veltaxio without ?admin shows blank page
  if (isVeltaxio && !isAdminRoute) return null;
  if (isAdminRoute) {
    if (screen === "loading") return (
      <div style={{ minHeight: "100vh", background: "#f8f7f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{css}</style>
        <div style={{ width: "40px", height: "40px", border: "2px solid #eee", borderTop: "2px solid #1a3a5c", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    );
    if (screen === "admin-dashboard") return <ExhaleDashboard adminToken={auth?.session?.getIdToken?.().getJwtToken?.() || ""} onLogout={() => { auth?.user?.signOut(); setAuth(null); setScreen("admin-login"); }} />;
    return <AdminLogin onLoggedIn={(a) => { setAuth(a); setScreen("admin-dashboard"); }} />;
  }

  // Splash while checking session
  if (screen === "loading") return (
    <div style={{ minHeight: "100vh", background: "#f8f7f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{css}</style>
      <div style={{ width: "40px", height: "40px", border: "2px solid #eee", borderTop: "2px solid #1a3a5c", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
    </div>
  );

  if (screen === "dashboard") return (
    <PatientDashboard authEmail={auth.email} cognitoUser={auth.user} onLogout={handleLogout} />
  );

  const goHome = () => setScreen("landing");

  if (screen === "landing")  return <Landing onStart={() => setScreen("signup")} onLogin={() => setScreen("login")} onPromoStart={() => { setIsPromo(true); setScreen("signup"); }} />;
  if (screen === "login")    return <Login onLoggedIn={handleLoggedIn} onSignUp={() => setScreen("signup")} onHome={goHome} />;
  if (screen === "signup")   return <CreateAccount onNext={a => { const { cognitoUser: cu, ...rest } = a; setAccount(rest); if (cu) setAuth(prev => ({ ...(prev || {}), user: cu })); setScreen(isPromo ? "upload" : "info"); }} onHome={goHome} />;
  if (screen === "info")     return <PersonalInfo onNext={p => { setPatient({ ...p, ...account }); setScreen(isPromo ? "upload" : "loa"); }} onHome={goHome} />;
  if (screen === "loa")      return <LOASigning patient={patient} isPromo={isPromo} onSigned={() => setScreen("upload")} onHome={goHome} />;
  if (screen === "upload")   return <UploadBill patient={patient} cognitoUser={auth?.user} patientEmail={account?.email || auth?.email || ""} onUploaded={(key) => { setS3Key(key); setScreen(isPromo ? "confirm" : "payment"); }} onHome={goHome} isPromo={isPromo} />;
  if (screen === "payment")  return <PaymentSetup patient={patient} s3Key={s3Key} onComplete={() => setScreen("confirm")} onHome={goHome} />;
  if (screen === "confirm")  return <Confirmation patient={patient} onHome={goHome} onDashboard={() => setScreen("dashboard")} isPromo={isPromo} />;
  return null;
}
